import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Heart, Sparkles, ArrowRight, Crown } from 'lucide-react';
import axios from 'axios';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No payment session found');
    }
  }, [sessionId]);

  const checkPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 3000; // 3 seconds

    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support if your payment was processed.');
      setPaymentStatus('error');
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`
      );

      const data = response.data;
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setPaymentStatus('error');
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('processing');
      setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Error verifying payment. Please contact support.');
      setPaymentStatus('error');
    }
  };

  const getProductType = () => {
    return paymentDetails?.metadata?.product_type || 'subscription';
  };

  const getProductName = () => {
    const productType = getProductType();
    if (productType === 'subscription') {
      const packageId = paymentDetails?.metadata?.package_id;
      if (packageId?.includes('premium')) {
        return 'Premium Subscription';
      } else if (packageId?.includes('vip')) {
        return 'VIP Subscription';
      }
      return 'Subscription';
    } else if (productType === 'boost') {
      const boostType = paymentDetails?.metadata?.boost_type;
      return `${boostType?.replace('_', ' ').toUpperCase()} Boost`;
    }
    return 'Purchase';
  };

  if (paymentStatus === 'checking' || paymentStatus === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {paymentStatus === 'checking' ? 'Verifying Payment...' : 'Processing Payment...'}
          </h2>
          <p className="text-gray-300">
            Please wait while we confirm your payment. This should only take a moment.
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-white/5 backdrop-blur-md border border-red-500/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xl">!</span>
              </div>
            </div>
            <CardTitle className="text-xl text-white">Payment Issue</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-300 mb-6">{error}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/subscription/plans')}
                className="w-full bg-rose-500 hover:bg-rose-600 text-white"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Continue to Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>
          
          <div className="animate-bounce mb-4">
            <Heart className="w-8 h-8 text-rose-400 mx-auto" />
          </div>
        </div>

        <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl">
          <CardHeader className="text-center border-b border-white/10">
            <CardTitle className="text-3xl font-bold text-white mb-2">
              🎉 Payment Successful!
            </CardTitle>
            <p className="text-gray-300 text-lg">
              Welcome to your enhanced dating experience
            </p>
          </CardHeader>

          <CardContent className="p-8">
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white">{getProductName()}</h3>
                  <p className="text-gray-300">Activated successfully</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-white">
                    ${(paymentDetails?.amount_total / 100).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-400 uppercase">
                    {paymentDetails?.currency}
                  </div>
                </div>
              </div>

              {getProductType() === 'subscription' && (
                <div className="flex items-center text-green-400 text-sm">
                  <Crown className="w-4 h-4 mr-2" />
                  <span>Your premium features are now active!</span>
                </div>
              )}
            </div>

            {/* Benefits Showcase */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Sparkles className="w-5 h-5 text-rose-400 mr-2" />
                What's unlocked for you:
              </h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                {getProductType() === 'subscription' ? (
                  <>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Unlimited AI-powered icebreakers</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Smart compatibility analysis</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Unlimited speed dating sessions</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Priority matching algorithm</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Profile optimization suggestions</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Real-time conversation coaching</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Your boost is now active!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/connections')}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-4 text-lg font-semibold shadow-lg hover:shadow-rose-500/25 transition-all duration-300"
              >
                Start Dating Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Complete Profile
                </Button>
                <Button
                  onClick={() => navigate('/subscription/status')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Manage Subscription
                </Button>
              </div>
            </div>

            {/* Support Info */}
            <div className="text-center mt-8 pt-6 border-t border-white/10">
              <p className="text-gray-400 text-sm">
                Need help? Contact us at support@shotclockdating.com
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;